import { useEffect, useRef, useState } from 'react';
import { T } from '../../../reportTokens';
import SectionEmpty from '../../../SectionEmpty';
import SectionError from '../../../SectionError';
import { getCurrencySymbol } from '../../../../../utils/currency';

export interface HourlyBarDataItem {
  label: string;
  value: number;
}

export interface HourlyBarChartProps {
  data: HourlyBarDataItem[];
  loading?: boolean;
  error?: boolean;
  onRetry?: () => void;
  kpiName?: string;
  /** Hex color for bars (e.g. T.green, T.accent) */
  barColor?: string;
  /** Height in px of the bar area (excludes peak-label space and footer) */
  chartHeight?: number;
  /** Formats individual bar values for the peak label and tooltip */
  formatValue?: (v: number) => string;
  /** When true, footer values include currencySymbol and 2-decimal format */
  showCurrency?: boolean;
  currencySymbol?: string;
  /** Min-height for empty/error states */
  minHeight?: number;
  /** Override the internally computed avg (sum/active-items) with a caller-supplied value (e.g. total/operatingHourCount) */
  avgOverride?: number;
}

const PEAK_LABEL_SPACE = 20;

const getNiceTicks = (maxVal: number, count = 4): number[] => {
  if (maxVal <= 0) return [0];
  const rawStep = maxVal / count;
  const magnitude = Math.pow(10, Math.floor(Math.log10(rawStep)));
  const normalized = rawStep / magnitude;
  const niceNorm = normalized < 1.5 ? 1 : normalized < 3.5 ? 2 : normalized < 7.5 ? 5 : 10;
  const step = niceNorm * magnitude;
  const ticks: number[] = [0];
  for (let t = step; t <= maxVal + step; t += step) {
    ticks.push(Math.round(t * 10) / 10);
    if (ticks[ticks.length - 1] >= maxVal) break;
    if (ticks.length > count + 2) break;
  }
  return ticks;
};

const hexToRgba = (hex: string, alpha: number): string => {
  const clean = hex.replace('#', '');
  const full = clean.length === 3
    ? clean.split('').map(c => c + c).join('')
    : clean;
  const r = parseInt(full.slice(0, 2), 16);
  const g = parseInt(full.slice(2, 4), 16);
  const b = parseInt(full.slice(4, 6), 16);
  return `rgba(${r},${g},${b},${alpha})`;
};

const defaultFormat = (v: number) => String(Math.round(v));

const HourlyBarChart = ({
  data,
  loading = false,
  error = false,
  onRetry,
  kpiName = 'hourly data',
  barColor = T.green,
  chartHeight = 160,
  formatValue = defaultFormat,
  showCurrency = false,
  currencySymbol = getCurrencySymbol(),
  minHeight = 180,
  avgOverride,
}: HourlyBarChartProps) => {
  const [tooltipIdx, setTooltipIdx] = useState(-1);
  const [containerWidth, setContainerWidth] = useState(0);
  const [isMobile, setIsMobile] = useState(() =>
    typeof window !== 'undefined' ? window.matchMedia('(max-width: 767px)').matches : false
  );
  const containerRef = useRef<HTMLDivElement>(null);

  // Dismiss tooltip when the page scrolls (desktop)
  useEffect(() => {
    const dismiss = () => setTooltipIdx(-1);
    window.addEventListener('scroll', dismiss, { passive: true });
    return () => window.removeEventListener('scroll', dismiss);
  }, []);

  useEffect(() => {
    const mql = window.matchMedia('(max-width: 767px)');
    const handler = (e: MediaQueryListEvent) => setIsMobile(e.matches);
    mql.addEventListener('change', handler);
    return () => mql.removeEventListener('change', handler);
  }, []);

  // Track container width so we can switch to compact labels / drop every-Nth label
  // when bars get cramped on narrow viewports (mobile).
  useEffect(() => {
    const node = containerRef.current;
    if (!node) return;
    setContainerWidth(node.getBoundingClientRect().width);
    if (typeof ResizeObserver === 'undefined') return;
    const observer = new ResizeObserver((entries) => {
      for (const entry of entries) {
        setContainerWidth(entry.contentRect.width);
      }
    });
    observer.observe(node);
    return () => observer.disconnect();
  }, []);

  const handleTouch = (idx: number) => {
    setTooltipIdx(prev => (prev === idx ? -1 : idx));
  };

  if (error) {
    return <SectionError kpiName={kpiName} onRetry={onRetry ?? (() => {})} minHeight={minHeight} />;
  }

  const hasData = data.some(d => d.value > 0);

  if (!loading && !hasData) {
    return <SectionEmpty kpiName={kpiName} minHeight={minHeight} />;
  }

  const peakValue = data.reduce((m, d) => Math.max(m, d.value), 0);
  const peakIndex = peakValue > 0 ? data.findIndex(d => d.value === peakValue) : -1;
  const useRankedColors = data.length >= 8;
  const sortedByValue = useRankedColors
    ? [...data].map((d, i) => ({ i, v: d.value })).sort((a, b) => b.v - a.v)
    : [];
  const rank2Index = sortedByValue.length > 1 ? sortedByValue[1].i : -1;
  const rank3Index = sortedByValue.length > 2 ? sortedByValue[2].i : -1;
  const activeItems = data.filter(d => d.value > 0);
  const avgValue = avgOverride !== undefined
    ? avgOverride
    : activeItems.length > 0
      ? activeItems.reduce((s, d) => s + d.value, 0) / activeItems.length
      : 0;
  const totalValue = data.reduce((s, d) => s + d.value, 0);

  const formatFooter = (v: number) => {
    if (showCurrency) {
      return `${currencySymbol}${Number(v).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
    }
    return formatValue(v);
  };

  const expandLabel = (lbl: string) =>
    lbl.replace(/(\d+)a$/, '$1 AM').replace(/(\d+)p$/, '$1 PM');

  // Responsive sizing — derived from the live container width. Until the
  // ResizeObserver fires, assume a conservative 280px mobile container so that
  // dense datasets (24 hourly bars) get a sane default stride immediately.
  const perBar = containerWidth > 0 && data.length > 0
    ? containerWidth / data.length
    : Math.min(32, 280 / Math.max(data.length, 1));
  const useCompactLabels = perBar < 30;
  // Minimum stride driven by data count alone prevents collisions even when
  // containerWidth hasn't been measured yet (first render / SSR).
  const minStrideByCount = data.length > 20 ? 4 : data.length > 14 ? 3 : data.length > 8 ? 2 : 1;
  const labelStride = Math.max(
    minStrideByCount,
    perBar < 14 ? 4 : perBar < 18 ? 3 : perBar < 24 ? 2 : 1,
  );
  // barPad controls the space between bars; bars themselves fill the remaining width
  const barPad = perBar < 14 ? 0.5 : perBar < 22 ? 1 : 2;
  const labelFontSize = useCompactLabels ? 9 : 8;

  const formatYAxis = (v: number): string => {
    if (v === 0) return '0';
    if (showCurrency) {
      if (v >= 1_000_000) return `${currencySymbol}${(v / 1_000_000).toFixed(1)}M`;
      if (v >= 1_000) return `${currencySymbol}${(v / 1_000).toFixed(1)}k`;
      return `${currencySymbol}${Math.round(v)}`;
    }
    if (v >= 1_000_000) return `${(v / 1_000_000).toFixed(1)}M`;
    if (v >= 1_000) return `${(v / 1_000).toFixed(1)}k`;
    return formatValue(v);
  };

  const yTicks = getNiceTicks(peakValue);
  const scaleMax = (yTicks.length > 0 ? yTicks[yTicks.length - 1] : peakValue) || 1;
  const yAxisWidth = showCurrency ? 52 : 36;

  if (loading) {
    return (
      <div style={{ display: 'flex', alignItems: 'flex-start' }}>
        {/* Y-axis skeleton */}
        <div style={{ width: yAxisWidth, flexShrink: 0, position: 'relative', height: chartHeight + PEAK_LABEL_SPACE, paddingTop: PEAK_LABEL_SPACE }}>
          {[0, 33, 66, 100].map((pct) => (
            <div key={pct} style={{ position: 'absolute', bottom: (pct / 100) * chartHeight, right: 6, width: 22, height: 8, borderRadius: 3, background: '#e8eaf0' }} />
          ))}
        </div>
        <div ref={containerRef} style={{ flex: 1, position: 'relative', display: 'flex', alignItems: 'flex-end', height: chartHeight + PEAK_LABEL_SPACE, paddingTop: PEAK_LABEL_SPACE }}>
          {Array.from({ length: 14 }).map((_, i) => (
            <div
              key={i}
              style={{
                flex: 1,
                height: `${18 + ((i * 41 + 13) % 80)}%`,
                borderRadius: '3px 3px 2px 2px',
                background: 'linear-gradient(90deg,#e8eaf0 25%,#f3f4f8 37%,#e8eaf0 63%)',
                backgroundSize: '800px 100%',
                animation: 'shimmer 1.4s ease infinite',
              }}
            />
          ))}
        </div>
      </div>
    );
  }

  const MIN_BAR_H = 3;

  // Cap bar width at the conservative fallback (280px assumed container) so bars stay
  // consistently thin regardless of whether containerWidth was measured at a narrow
  // (initial load, sidebar animating) or wide (post-navigation remount, settled layout) value.
  const fallbackPerBar = Math.min(32, 280 / Math.max(data.length, 1));
  const fallbackBarPad = fallbackPerBar < 14 ? 0.5 : fallbackPerBar < 22 ? 1 : 2;
  const barMaxWidth = fallbackPerBar - fallbackBarPad * 2;

  // Pre-compute avg line position. Clamp the display position so the dashed line
  // and its label never overlap the x-axis label row (~14px tall at the bottom).
  const avgLinePx = avgValue > 0 && scaleMax > 0 ? (avgValue / scaleMax) * chartHeight : 0;
  const X_AXIS_ROW_H = labelFontSize + 6; // approx height of x-axis label row
  const avgLineDisplayPx = Math.max(avgLinePx, X_AXIS_ROW_H);
  const avgLabelTop = -8;

  return (
    <div>
      {/* Chart area with Y-axis */}
      <div style={{ display: 'flex', alignItems: 'flex-start' }}>
        {/* Y-axis labels */}
        <div
          style={{
            width: yAxisWidth,
            flexShrink: 0,
            position: 'relative',
            height: chartHeight + PEAK_LABEL_SPACE,
            paddingTop: PEAK_LABEL_SPACE,
          }}
        >
          {yTicks.map((tick) => (
            <div
              key={tick}
              style={{
                position: 'absolute',
                bottom: tick === 0 ? 0 : (tick / scaleMax) * chartHeight,
                right: 6,
                transform: tick === 0 ? 'none' : 'translateY(50%)',
                fontSize: 9,
                color: T.t3,
                lineHeight: 1,
                whiteSpace: 'nowrap',
                textAlign: 'right',
              }}
            >
              {formatYAxis(tick)}
            </div>
          ))}
        </div>

        {/* Bars + gridlines */}
        <div
          ref={containerRef}
          style={{
            flex: 1,
            minWidth: 0,
            position: 'relative',
            display: 'flex',
            alignItems: 'flex-end',
            height: chartHeight + PEAK_LABEL_SPACE,
            paddingTop: PEAK_LABEL_SPACE,
          }}
          onMouseLeave={() => setTooltipIdx(-1)}
        >
          {/* Horizontal gridlines at each Y-axis tick */}
          {yTicks.filter(t => t > 0).map((tick) => (
            <div
              key={tick}
              style={{
                position: 'absolute',
                left: 0,
                right: 0,
                bottom: (tick / scaleMax) * chartHeight,
                borderTop: `1px solid ${T.divider}`,
                pointerEvents: 'none',
                zIndex: 0,
              }}
            />
          ))}

        {/* Dashed average line */}
        {avgValue > 0 && peakValue > 0 && (
          <div
            style={{
              position: 'absolute',
              left: 0,
              right: 0,
              bottom: `${avgLineDisplayPx}px`,
              borderTop: `1px dashed ${T.t3}`,
              pointerEvents: 'none',
              zIndex: 3,
            }}
          >
            <div
              style={{
                position: 'absolute',
                right: 0,
                top: avgLabelTop,
                fontSize: 9,
                color: T.t2,
                background: T.surface,
                padding: '0 4px',
                fontWeight: 500,
                whiteSpace: 'nowrap',
              }}
            >
              Avg: {formatFooter(avgValue)}
            </div>
          </div>
        )}

        {/* Bars */}
        {data.map((item, index) => {
          const max = peakValue || 1;
          const h = (item.value / scaleMax) * chartHeight;
          const isPeak = index === peakIndex && item.value > 0;
          const hasValue = item.value > 0;
          const renderedH = Math.max(h, MIN_BAR_H);
          const isTooltipVisible = index === tooltipIdx;

          const barBg = (() => {
            if (isPeak) return barColor;
            if (isTooltipVisible) return hexToRgba(barColor, 0.85);
            if (useRankedColors) {
              if (index === rank2Index) return hexToRgba(barColor, 0.75);
              if (index === rank3Index) return hexToRgba(barColor, 0.55);
              return hexToRgba(barColor, 0.40);
            }
            const opacity = hasValue ? 0.15 + (item.value / max) * 0.45 : 0.12;
            return hexToRgba(barColor, opacity);
          })();

          // Flip tooltip alignment at edges so it never overflows the container
          const edgeThreshold = Math.ceil(data.length * 0.2);
          const isNearRightEdge = index >= data.length - edgeThreshold;
          const isNearLeftEdge = index < edgeThreshold;
          const tooltipLeft = isNearRightEdge ? 'auto' : isNearLeftEdge ? '0' : '50%';
          const tooltipRight = isNearRightEdge ? '0' : 'auto';
          const tooltipTransform = (!isNearLeftEdge && !isNearRightEdge) ? 'translateX(-50%)' : 'none';

          return (
            <div
              key={index}
              style={{
                flex: 1,
                minWidth: 0,
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'flex-end',
                alignItems: 'center',
                gap: 2,
                height: '100%',
                position: 'relative',
                zIndex: isTooltipVisible ? 40 : 2,
                cursor: 'pointer',
                paddingLeft: barPad,
                paddingRight: barPad,
                boxSizing: 'border-box',
              }}
              onMouseEnter={() => setTooltipIdx(index)}
              onTouchStart={() => handleTouch(index)}
            >
              {/* Hover / touch tooltip */}
              {isTooltipVisible && (
                <div
                  style={{
                    position: 'absolute',
                    bottom: renderedH + 22,
                    left: tooltipLeft,
                    right: tooltipRight,
                    transform: tooltipTransform,
                    background: T.surface,
                    border: `1px solid ${T.cardBorder}`,
                    borderRadius: 6,
                    padding: '4px 8px',
                    boxShadow: '0 4px 12px rgba(0,0,0,0.10)',
                    fontSize: 11,
                    color: T.t1,
                    pointerEvents: 'none',
                    zIndex: 30,
                    whiteSpace: 'nowrap',
                    lineHeight: 1.3,
                  }}
                >
                  <div style={{ fontWeight: 500, color: T.t3, fontSize: 10 }}>{expandLabel(item.label)}</div>
                  <div style={{ fontWeight: 700, color: barColor }}>{formatFooter(item.value)}</div>
                </div>
              )}

              {/* Peak-only value label — absolutely positioned so it never reduces the bar's rendered height */}
              {isPeak && (
                <div style={{ position: 'absolute', bottom: renderedH + 16, left: '50%', transform: 'translateX(-50%)', fontSize: 9, fontWeight: 700, color: barColor, whiteSpace: 'nowrap', lineHeight: 1, zIndex: 5 }}>
                  {formatFooter(item.value)}
                </div>
              )}

              {/* Bar */}
              <div
                style={{
                  width: '100%',
                  maxWidth: Math.min(perBar - barPad * 2, barMaxWidth),
                  height: renderedH,
                  borderRadius: '4px 4px 2px 2px',
                  background: barBg,
                  transition: 'background 0.15s',
                }}
              />

              {/* X-axis label — peak always shown; others skipped when bars get cramped */}
              <div
                style={{
                  fontSize: isMobile ? 7 : labelFontSize,
                  color: isPeak ? barColor : T.t3,
                  marginTop: 4,
                  fontWeight: isPeak ? 600 : 400,
                  whiteSpace: 'nowrap',
                  height: labelFontSize + 2,
                  lineHeight: 1,
                  visibility: (isPeak || index % labelStride === 0) ? 'visible' : 'hidden',
                  transform: isMobile ? 'rotate(-45deg)' : 'none',
                  transformOrigin: isMobile ? 'center top' : 'center center',
                }}
              >
                {useCompactLabels ? item.label : expandLabel(item.label)}
              </div>
            </div>
          );
        })}
        </div>{/* end bars+gridlines */}
      </div>{/* end chart area with Y-axis */}

      {/* Footer: Peak | Avg | Total */}
      {peakValue > 0 && (
        <div
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            marginTop: 10,
            paddingTop: 8,
            borderTop: `1px solid ${T.divider}`,
            fontSize: 11,
            color: T.t3,
            flexWrap: 'wrap',
            gap: 4,
          }}
        >
          <span>
            Peak:{' '}
            <span style={{ color: barColor, fontWeight: 600 }}>
              {peakIndex >= 0 ? expandLabel(data[peakIndex].label) : '--'}
            </span>
            {peakIndex >= 0 ? ` — ${formatFooter(peakValue)}` : ''}
          </span>
          <span>
            Avg:{' '}
            <span style={{ color: T.t1, fontWeight: 600 }}>
              {avgValue > 0 ? formatFooter(avgValue) : '--'}
            </span>
          </span>
          <span>
            Total:{' '}
            <span style={{ color: T.t1, fontWeight: 600 }}>{formatFooter(totalValue)}</span>
          </span>
        </div>
      )}
    </div>
  );
};

export default HourlyBarChart;
